import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Bubble } from 'tea-component';
import DatalabGrid from '@/pages/datalab/components/DatalabGrid';
import useVirtualReferenceElement from '@/pages/datalab/utils/hooks/useVirtualReferenceElement';
import SearchSingleSelect from '@/pages/datalab/components/SearchSingleSelect';

type THoverInfo = string | null;

interface IDatabaseOption {
  value: string;
  label?: string;
  text?: ReactNode;
  driver?: string;
  databaseName?: string;
  schema?: string[] | string;
  [key: string]: any;
}

interface IDatabaseSelectWithOptionBubbleProps {
  selectorName: string;
  loading?: boolean;
  error?: any;
  options: IDatabaseOption[];
  className?: string;
  infoConfig?: any;
  value: string;
  onChange: (value: string, context?: any) => void;
  formatMessage: (descriptor: { id: string }) => string;
}

const EMPTY_TEXT = '-';

const DatabaseOptionDetail: React.FC<{
  option?: IDatabaseOption;
  formatMessage: (descriptor: { id: string }) => string;
}> = ({ option, formatMessage }) => {
  if (!option) return null;

  const { databaseName, driver, host, port, schema } = option;
  const nameText = `${databaseName}${
    typeof schema === 'string' && schema ? `(${schema})` : ''
  }`;

  const rows = [
    {
      label: formatMessage({ id: 'dbl_create_dashboard_dataset_detail_name' }),
      value: nameText || EMPTY_TEXT,
    },
    {
      label: formatMessage({ id: 'dbl_create_dashboard_dataset_detail_database_type' }),
      value: driver || EMPTY_TEXT,
    },
    {
      label: formatMessage({ id: 'dbl_multi_database_host' }),
      value: host || EMPTY_TEXT,
    },
    {
      label: formatMessage({ id: 'dbl_multi_database_port' }),
      value: port || EMPTY_TEXT,
    },
  ];

  return (
    <div className="dbl-left-data-select-option-detail">
      <DatalabGrid
        colCount={2}
        gap="8px 20px"
        columnWidthMode={['max-content', '1fr']}
        verticalAlign="start"
      >
        {rows.map((row) => (
          <React.Fragment key={row.label}>
            <DatalabGrid.Item className="dbl-left-data-select-option-detail-label">
              {row.label}
            </DatalabGrid.Item>
            <DatalabGrid.Item className="dbl-left-data-select-option-detail-value">
              {row.value}
            </DatalabGrid.Item>
          </React.Fragment>
        ))}
      </DatalabGrid>
    </div>
  );
};

const DatabaseSelectWithOptionBubble: React.FC<IDatabaseSelectWithOptionBubbleProps> = (props) => {
  const { options, formatMessage } = props;

  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const { referenceElement, setAnchor } = useVirtualReferenceElement({
    refreshAfterAnchorInRaf: false,
    scrollContainerRef: scrollContainerRef as React.RefObject<HTMLElement>,
  });
  const [hoverInfo, setHoverInfo] = useState<THoverInfo>(null);

  const { enrichedOptions, optionMap } = useMemo(() => {
    const nextOptionMap = new Map<string, IDatabaseOption>();
    const nextEnrichedOptions: IDatabaseOption[] = [];

    (options || []).forEach((opt) => {
      nextOptionMap.set(opt.value, opt);
      nextEnrichedOptions.push({
        ...opt,
        text: (
          <div
            data-option-value={opt.value}
            className="dbl-left-data-select-option"
          >
            {opt.text}
          </div>
        ),
      });
    });

    return { enrichedOptions: nextEnrichedOptions, optionMap: nextOptionMap };
  }, [options]);

  // 事件委托：挂在 document 上，避免依赖下拉容器的挂载时机。
  useEffect(() => {
    const handleMouseOver = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;
      const optionEl = target.closest('[data-option-value]') as HTMLElement | null;
      if (!optionEl) return;
      const container = optionEl.closest('.dbl-left-data-select-database-overlay') as HTMLElement | null;
      if (!container) return;

      const value = optionEl.getAttribute('data-option-value');
      if (!value) return;

      setHoverInfo((prevHoverInfo) => {
        if (prevHoverInfo === value) {
          return prevHoverInfo;
        }
        (scrollContainerRef as React.MutableRefObject<HTMLElement | null>).current = container;
        setAnchor(optionEl);
        return value;
      });
    };

    const handleMouseOut = (e: MouseEvent): void => {
      const target = e.target;
      if (!(target instanceof Element)) return;

      const container = target.closest('.dbl-left-data-select-database-overlay');
      if (!container) return;

      const relatedTarget = e.relatedTarget;
      if (
        relatedTarget instanceof Element &&
        relatedTarget.closest('.dbl-left-data-select-database-overlay')
      ) {
        return;
      }

      setHoverInfo(null);
      setAnchor(null);
    };

    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);

    return () => {
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
      (scrollContainerRef as React.MutableRefObject<HTMLElement | null>).current = null;
    };
  }, [setAnchor]);

  return (
    <>
      <SearchSingleSelect
        {...props}
        options={enrichedOptions}
        selectProps={{
          overlayClassName: 'dbl-left-data-select-database-overlay',
          ...((props as any)?.selectProps || {}),
        }}
      />
      <Bubble
        referenceElement={referenceElement}
        className="dbl-left-data-select-database-bubble"
        dark
        placement="right"
        visible={Boolean(hoverInfo)}
        content={(
          <DatabaseOptionDetail
            option={hoverInfo ? optionMap.get(hoverInfo) : undefined}
            formatMessage={formatMessage}
          />
        )}
      >
        <span />
      </Bubble>
    </>
  );
};

export default DatabaseSelectWithOptionBubble;

